import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GstModeProvider } from "@/context/GstModeContext";
import { AppShell } from "@/components/layout/AppShell";
import AuthPage from "@/pages/AuthPage";
import LandingPage from "@/pages/LandingPage";
import WaitlistPage from "@/pages/WaitlistPage";
const SignupPage = lazy(() => import("@/pages/SignupPage"));
import DashboardPage from "@/pages/DashboardPage";
import ContractorDashboardPage from "@/pages/ContractorDashboardPage";
import { Building2 } from "lucide-react";

// Performance: every non-landing route is code-split so the first paint only
// downloads the shell + dashboard. Each page loads on demand (and is cached).
const ClientProgressPage = lazy(() => import("@/pages/ClientProgressPage"));
const JobsPage = lazy(() => import("@/pages/JobsPage"));
const JobDetailPage = lazy(() => import("@/pages/JobDetailPage"));
const SchedulePage = lazy(() => import("@/pages/SchedulePage"));
const ContractorsPage = lazy(() => import("@/pages/ContractorsPage"));
const SuppliersPage = lazy(() => import("@/pages/SuppliersPage"));
const ClientsPage = lazy(() => import("@/pages/ClientsPage"));
const ClientDetailPage = lazy(() => import("@/pages/ClientDetailPage"));
const ClientUpdatesPage = lazy(() => import("@/pages/ClientUpdatesPage"));
const ClientUpdateBuilderPage = lazy(() => import("@/pages/ClientUpdateBuilderPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const RendersPage = lazy(() => import("@/pages/RendersPage"));
const QuickQuotePage = lazy(() => import("@/pages/QuickQuotePage"));
const QuotesPage = lazy(() => import("@/pages/QuotesPage"));
const QuoteEditorPage = lazy(() => import("@/pages/QuoteEditorPage"));
const RateBookPage = lazy(() => import("@/pages/RateBookPage"));
const BoqTemplatesPage = lazy(() => import("@/pages/BoqTemplatesPage"));
const BOQPage = lazy(() => import("@/pages/BOQPage"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const CRMPage = lazy(() => import("@/pages/CRMPage"));
const ApprovalsPage = lazy(() => import("@/pages/ApprovalsPage"));
const CashFlowPage = lazy(() => import("@/pages/CashFlowPage"));
const ProgressClaimsPage = lazy(() => import("@/pages/ProgressClaimsPage"));
const SiteDiaryPage = lazy(() => import("@/pages/SiteDiaryPage"));
const FinancialsPage = lazy(() => import("@/pages/FinancialsPage"));
const TransactionsPage = lazy(() => import("@/pages/TransactionsPage"));
const BankFeedCallbackPage = lazy(() => import("@/pages/BankFeedCallbackPage"));
const DeliveriesPage = lazy(() => import("@/pages/DeliveriesPage"));
const PurchaseOrdersPage = lazy(() => import("@/pages/PurchaseOrdersPage"));
const TimesheetsPage = lazy(() => import("@/pages/TimesheetsPage"));
const CostBasePage = lazy(() => import("@/pages/CostBasePage"));
const InvoiceInboxPage = lazy(() => import("@/pages/InvoiceInboxPage"));
const BookingRespondPage = lazy(() => import("@/pages/BookingRespondPage"));
const SowAcknowledgePage = lazy(() => import("@/pages/SowAcknowledgePage"));
const InspectionRespondPage = lazy(() => import("@/pages/InspectionRespondPage"));
const QuoteQueryReplyPage = lazy(() => import("@/pages/QuoteQueryReplyPage"));
const SurveyorsPage = lazy(() => import("@/pages/SurveyorsPage"));
const VariationApprovalPage = lazy(() => import("@/pages/VariationApprovalPage"));
const PoConfirmPage = lazy(() => import("@/pages/PoConfirmPage"));
const RfiRespondPage = lazy(() => import("@/pages/RfiRespondPage"));
const PublicDefectPage = lazy(() => import("@/pages/PublicDefectPage"));
const PublicHandoverPage = lazy(() => import("@/pages/PublicHandoverPage"));
const SiteWalkPage = lazy(() => import("@/pages/SiteWalkPage"));
const QuoteAcceptPage = lazy(() => import("@/pages/QuoteAcceptPage"));
const QuoteSubmitPage = lazy(() => import("@/pages/QuoteSubmitPage"));
const FieldViewPage = lazy(() => import("@/pages/FieldViewPage"));
const RFIsPage = lazy(() => import("@/pages/RFIsPage"));
const CodeExpertPage = lazy(() => import("@/pages/CodeExpertPage"));
const JobQuotesPage = lazy(() => import("@/pages/JobQuotesPage"));
const AssignmentsPage = lazy(() => import("@/pages/AssignmentsPage"));
const ConsultantsPage = lazy(() => import("@/pages/ConsultantsPage"));
const PortalAccessPage = lazy(() => import("@/pages/PortalAccessPage"));
const QuoteComparisonPage = lazy(() => import("@/pages/QuoteComparisonPage"));
const QuoteComparisonDetailPage = lazy(() => import("@/pages/QuoteComparisonDetailPage"));
const HRPage = lazy(() => import("@/pages/HRPage"));
const CommsPage = lazy(() => import("@/pages/CommsPage"));
const NoticesPage = lazy(() => import("@/pages/NoticesPage"));
const NoticeAcknowledgePage = lazy(() => import("@/pages/NoticeAcknowledgePage"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));
const ExtPortalPage = lazy(() => import("@/pages/ExtPortalPage"));
const PortalSetupPage = lazy(() => import("@/pages/PortalSetupPage"));
const AccountSetupPage = lazy(() => import("@/pages/AccountSetupPage"));
const LandlordPage = lazy(() => import("@/pages/LandlordPage"));
const OnboardingSignPage = lazy(() => import("@/pages/OnboardingSignPage"));
const ContractsPage = lazy(() => import("@/pages/ContractsPage"));
const ContractEditorPage = lazy(() => import("@/pages/ContractEditorPage"));
const HRTemplateEditorPage = lazy(() => import("@/pages/HRTemplateEditorPage"));

const FullPageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="flex h-12 w-12 animate-pulse items-center justify-center rounded-xl bg-primary text-primary-foreground">
        <Building2 className="h-6 w-6" />
      </div>
      <p className="text-sm text-muted-foreground">Loading BuiltOS…</p>
    </div>
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) {
    // Signed-out visitors to the root see the public BuiltOS waitlist page (Phase 163);
    // deep links still go to sign-in so they return to the page they wanted.
    if (location.pathname === "/") return <WaitlistPage />;
    return <Navigate to="/auth" replace />;
  }
  return children;
};

// Contractors land on their own dashboard; homeowners go to their portal; everyone else gets the command center
const HomeRoute = () => {
  const { user } = useAuth();
  if (user?.role === "contractor") return <ContractorDashboardPage />;
  if (user?.role === "client") return <Navigate to="/portal" replace />;
  if (user?.role === "supplier") return <Navigate to="/supplier" replace />;
  if (user?.role === "consultant") return <Navigate to="/consultant" replace />;
  if (user?.role === "surveyor") return <Navigate to="/surveyor" replace />;
  return <DashboardPage />;
};

// Homeowner (client) accounts never see the builder shell — they always land on /portal.
// Phase 155 / 164: supplier / consultant / surveyor portal accounts likewise land on their own portal.
const StaffArea = () => {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/portal" replace />;
  if (user?.role === "supplier") return <Navigate to="/supplier" replace />;
  if (user?.role === "consultant") return <Navigate to="/consultant" replace />;
  if (user?.role === "surveyor") return <Navigate to="/surveyor" replace />;
  return <AppShell />;
};

function App() {
  return (
    <AuthProvider>
      <GstModeProvider>
      <BrowserRouter>
        <Suspense fallback={<FullPageLoader />}>
          <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/booking/:token" element={<BookingRespondPage />} />
          <Route path="/sow/:token" element={<SowAcknowledgePage />} />
          <Route path="/notice/:token" element={<NoticeAcknowledgePage />} />
          <Route path="/inspection/:token" element={<InspectionRespondPage />} />
          <Route path="/quote-query/:token" element={<QuoteQueryReplyPage />} />
          <Route path="/variation/:token" element={<VariationApprovalPage />} />
          <Route path="/po/:token" element={<PoConfirmPage />} />
          <Route path="/rfi/:token" element={<RfiRespondPage />} />
          <Route path="/defect/:token" element={<PublicDefectPage />} />
          <Route path="/handover/:token" element={<PublicHandoverPage />} />
          {/* Phone-first PC site walk — full-screen (no builder shell), staff only */}
          <Route
            path="/jobs/:jobId/walk"
            element={
              <Protected>
                <SiteWalkPage />
              </Protected>
            }
          />
          <Route path="/quote/:token" element={<QuoteAcceptPage />} />
          <Route path="/submit-quote/:token" element={<QuoteSubmitPage />} />
          <Route path="/progress/:token" element={<ClientProgressPage />} />
          <Route path="/portal/setup/:token" element={<PortalSetupPage />} />
          <Route path="/welcome/setup/:token" element={<AccountSetupPage />} />
          <Route path="/onboarding/:token" element={<OnboardingSignPage />} />
          <Route
            path="/portal"
            element={
              <Protected>
                <ClientPortalPage />
              </Protected>
            }
          />
          <Route
            path="/supplier"
            element={
              <Protected>
                <ExtPortalPage role="supplier" />
              </Protected>
            }
          />
          <Route
            path="/consultant"
            element={
              <Protected>
                <ExtPortalPage role="consultant" />
              </Protected>
            }
          />
          <Route
            path="/surveyor"
            element={
              <Protected>
                <ExtPortalPage role="surveyor" />
              </Protected>
            }
          />
          <Route
            path="/field"
            element={
              <Protected>
                <FieldViewPage />
              </Protected>
            }
          />
          <Route
            element={
              <Protected>
                <StaffArea />
              </Protected>
            }
          >
            <Route path="/" element={<HomeRoute />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/cashflow" element={<CashFlowPage />} />
            <Route path="/progress-claims" element={<ProgressClaimsPage />} />
            <Route path="/site-diary" element={<SiteDiaryPage />} />
            <Route path="/financials" element={<FinancialsPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            {/* Basiq Consent UI hands back here (opened in its own tab from Settings → Bank feeds) */}
            <Route path="/bank-feeds/callback" element={<BankFeedCallbackPage />} />
            <Route path="/deliveries" element={<DeliveriesPage />} />
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/timesheets" element={<TimesheetsPage />} />
            <Route path="/cost-base" element={<CostBasePage />} />
            <Route path="/surveyors" element={<SurveyorsPage />} />
            <Route path="/consultants" element={<ConsultantsPage />} />
            <Route path="/portal-access" element={<PortalAccessPage />} />
            <Route path="/quote-comparison" element={<QuoteComparisonPage />} />
            <Route path="/quote-comparison/:id" element={<QuoteComparisonDetailPage />} />
            <Route path="/hr" element={<HRPage />} />
            <Route path="/hr/templates/:id" element={<HRTemplateEditorPage />} />
            <Route path="/comms" element={<CommsPage />} />
            <Route path="/notices" element={<NoticesPage />} />
            <Route path="/rfis" element={<RFIsPage />} />
            <Route path="/code-expert" element={<CodeExpertPage />} />
            <Route path="/job-quotes" element={<JobQuotesPage />} />
            <Route path="/invoice-inbox" element={<InvoiceInboxPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/assignments" element={<AssignmentsPage />} />
            <Route path="/jobs/:jobId" element={<JobDetailPage />} />
            <Route path="/quick-quote" element={<QuickQuotePage />} />
            <Route path="/quotes" element={<QuotesPage />} />
            <Route path="/quotes/:quoteId" element={<QuoteEditorPage />} />
            <Route path="/rate-book" element={<RateBookPage />} />
            <Route path="/boq-templates" element={<BoqTemplatesPage />} />
            <Route path="/boq" element={<BOQPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/crm" element={<CRMPage />} />
            <Route path="/contracts" element={<ContractsPage />} />
            <Route path="/contracts/:id" element={<ContractEditorPage />} />
            <Route path="/renders" element={<RendersPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="/client-updates" element={<ClientUpdatesPage />} />
            <Route path="/client-updates/new" element={<ClientUpdateBuilderPage />} />
            <Route path="/client-updates/:updateId/edit" element={<ClientUpdateBuilderPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/contractors" element={<ContractorsPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/landlord" element={<LandlordPage />} />
            <Route path="/help" element={<HelpPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" closeButton />
      </GstModeProvider>
    </AuthProvider>
  );
}

export default App;
